import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * Catches errors thrown while rendering a route — most importantly the
 * "ChunkLoadError" that occurs when a lazily-loaded page chunk can't be fetched
 * (typically because a new build was deployed and the user's tab still
 * references the old, now-deleted chunk filenames). Instead of a blank white
 * screen, the user gets a clear prompt to refresh and pick up the new build.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const msg = `${error?.name || ''} ${error?.message || ''}`;
    const isChunkError = /ChunkLoadError|Loading chunk|dynamically imported module|Failed to fetch/i.test(msg);
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error, info) {
    // Surface for debugging; not user-facing.
    console.error('Route render error:', error, info?.componentStack);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="route-error-boundary">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold">
            {this.state.isChunkError ? 'A new version is available' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {this.state.isChunkError
              ? "This page was updated since you loaded the app. Refresh to get the latest version."
              : "We hit an unexpected error loading this page. Refreshing usually fixes it."}
          </p>
          <Button onClick={this.handleRefresh} className="w-full" data-testid="route-error-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
